/**
 * Event Trigger for Media Query Changes
 */
import { debounce } from 'lodash-es';

const MediaQueryEvent = {
  queries: [],
  current: '',

  init() {
    if (this.isInitialized === true) {
      return;
    }
    this.isInitialized = true;

    const meta = document.querySelector('meta.the-mq');
    if (!meta) {
      const metaEl = document.createElement('meta');
      metaEl.setAttribute('content', '');
      metaEl.classList.add('the-mq');
      document.head.appendChild(metaEl);
    }

    const { fontFamily } = window.getComputedStyle(document.querySelector('meta.the-mq'));
    const parsedParams = new URLSearchParams(fontFamily.substring(1, fontFamily.length - 1));
    Array.from(parsedParams.entries()).forEach(([key, value]) => {
      this.queries.push({
        name: key,
        value: `only screen and (min-width: ${value})`,
      });
    });

    this.current = this.getCurrentSize();

    this.watcher();
  },

  /**
   * Gets the media query of a breakpoint.
   * @function
   * @param {String} size - Name of the breakpoint to get.
   * @returns {String|null} - The media query of the breakpoint or null
   */
  get(size) {
    const found = this.queries.filter((query) => size === query.name).pop();
    const { value } = found || {};
    return value || null;
  },

  /**
   * Checks if the screen is at least as wide as a breakpoint.
   * @function
   * @param {String} size - Name of the breakpoint to check.
   * @returns {Boolean} `true` if the breakpoint matches, `false` if it's smaller.
   */
  atLeast(size) {
    const query = this.get(size);

    if (query) {
      return window.matchMedia(query).matches;
    }

    return false;
  },

  /**
   * Returns the name of the breakpoint related to the given value.
   * @function
   * @private
   * @param {String|Object} value - Breakpoint name or query object.
   * @returns {String} Name of the breakpoint.
   */
  getQueryName(value) {
    if (typeof value === 'string') return value;
    if (typeof value === 'object') return value.name;
    throw new TypeError(`
      Invalid value passed to MediaQuery._getQueryName().
      Expected a breakpoint name (String) or a breakpoint query (Object), got "${value}" (${typeof value})
    `);
  },

  /**
   * Gets the current breakpoint name by testing every breakpoint and returning
   * the last one to match (the biggest one).
   * @function
   * @private
   * @returns {String} Name of the current breakpoint.
   */
  getCurrentSize() {
    let matched;

    for (let i = 0; i < this.queries.length; i += 1) {
      const query = this.queries[i];

      if (window.matchMedia(query.value).matches) {
        matched = query;
      }
    }

    return matched && this.getQueryName(matched);
  },

  /**
   * Activates the breakpoint watcher, which fires an event on the window
   * whenever the breakpoint changes.
   * @function
   * @private
   */
  watcher() {
    window.addEventListener(
      'resize',
      debounce(() => {
        const newSize = this.getCurrentSize();
        const currentSize = this.current;

        if (newSize !== currentSize) {
          this.current = newSize;
          window.dispatchEvent(new CustomEvent(
            'mediaqueryChanged',
            {
              detail: {
                newSize,
                currentSize,
              },
            },
          ));
        }
      }, 100),
    );
  },
};

MediaQueryEvent.init();

export default MediaQueryEvent;
